import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  getPledgeReleaseCaseById,
  createGoldReceipt,
  updateGoldReceipt,
  getGoldReceiptByCase,
} from '../api';
import {
  Form, Input, InputNumber, DatePicker, Button, Card, Typography,
  Row, Col, Spin, message, Space, Table, Divider, Alert, Tag, Tooltip,
} from 'antd';
import { CloseOutlined, PlusOutlined, GoldOutlined, CameraOutlined, SyncOutlined } from '@ant-design/icons';
import { fetchHallmarkGoldRate, invalidateGoldPriceCache } from '../../../services/goldPriceService';
import { useKaratDeductions } from '../../system/hooks/useKaratDeductions';
import dayjs from 'dayjs';
import {
  recalcCaseGoldItem, calculateCaseGoldTotals, calculateCaseProcessingFee, calculateCaseFinalAmount,
  formatWeight4, formatAmount4, calculateCaseRoundOff, formatRoundOff,
} from '../../../utils/goldCalculation';
import { fetchBranchProcessingFeeToggleVisibility } from '../../treasury/api';
import GoldImageCapture from '../../billing/components/GoldImageCapture';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);

const formatWholeAmount = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price || 0);

const formatRoundingAmount = (amount) => {
  const formatted = new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.abs(amount || 0));
  if (amount > 0) {
    return `+₹ ${formatted}`;
  } else if (amount < 0) {
    return `-₹ ${formatted}`;
  }
  return `₹ ${formatted}`;
};

// Processing fee is exact (not rounded to a rupee), so it shows 4 decimals.
const formatProcessingFeeDisplay = (amount) => {
  const formatted = formatAmount4(Math.abs(amount || 0));
  if (amount > 0) {
    return `-₹ ${formatted}`;
  } else if (amount < 0) {
    return `-₹ ${formatted}`;
  }
  return `₹ ${formatted}`;
};

const roundUpFeeDisplay = (amount) => {
  const formatted = new Intl.NumberFormat('en-IN', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.abs(amount || 0));
  if (amount > 0) {
    return `+₹ ${formatted}`;
  } else if (amount < 0) {
    return `-₹ ${formatted}`;
  }
  return `₹ ${formatted}`;
};

const DEFAULT_PURITY = 'Gold 916';

const EMPTY_GOLD_ITEM = {
  qty: 1,
  itemName: '',
  purityName: DEFAULT_PURITY,
  purity: 0,
  grossWeight: 0,
  stoneWaxDeduction: 0,
  dustDeduction: 0,
  netWeight: 0,
  estimatedRatePerGram: 0,
  totalPrice: 0,
};

const GoldReceiptForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  // The same form serves creating the first valuation and correcting it later.
  // Correction is only reachable on /gold-receipt/edit, and only goes through
  // if the case carries an approved reconciliation — the backend enforces that.
  const isEditMode = location.pathname.endsWith('/gold-receipt/edit');

  const [caseData, setCaseData] = useState(null);
  const [existingReceipt, setExistingReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { getChannelRate } = useKaratDeductions();

  // Gold items state
  const [goldItems, setGoldItems] = useState([{ ...EMPTY_GOLD_ITEM }]);
  const [remarks, setRemarks] = useState('');
  const [valuationDate, setValuationDate] = useState(dayjs());
  const [goldImages, setGoldImages] = useState([]);
  const [roundingUpAmount, setRoundingUpAmount] = useState(0);
  const [processingFeePercent, setProcessingFeePercent] = useState(0);
  const [canViewProcessingFee, setCanViewProcessingFee] = useState(true);

  // Live gold rate state (stores rates with karat mappings)
  const [liveRates, setLiveRates] = useState(null);   // { rates: [{ karat, purity, perGram, per10gram }, ...], date }
  const [rateLoading, setRateLoading] = useState(false);

  const loadLiveRate = async (silent = false) => {
    if (!silent) setRateLoading(true);
    const rateData = await fetchHallmarkGoldRate();
    if (rateData) setLiveRates(rateData);
    setRateLoading(false);
    return rateData;
  };

  const handleRefreshRate = async () => {
    invalidateGoldPriceCache();
    const rateData = await loadLiveRate();
    if (rateData) {
      // Apply new rate to all rows - use default 22k rate
      const defaultRateObj = rateData.rates?.find(r => r.purity === DEFAULT_PURITY);
      const defaultRate = getChannelRate(defaultRateObj, 'pledge');
      setGoldItems((prev) =>
        prev.map((item) => {
          const updated = { ...item, estimatedRatePerGram: defaultRate };
          return recalcCaseGoldItem(updated);
        }),
      );
      messageApi.success(`Live rate updated: ₹${defaultRate}/gm`);
    } else {
      messageApi.warning('Could not refresh live rate');
    }
  };

  // Load case and pre-populate items from estimated gold items
  useEffect(() => {
    if (!caseId) {
      setLoading(false);
      return;
    }
    const loadCase = async () => {
      setLoading(true);
      // Fetch live rate in parallel
      const [res, rate, receiptRes] = await Promise.all([
        getPledgeReleaseCaseById(caseId).catch((e) => { throw e; }),
        loadLiveRate(true),
        isEditMode ? getGoldReceiptByCase(caseId).catch(() => null) : Promise.resolve(null),
      ]);
      try {
        const c = res.data;
        setCaseData(c);
        const receipt = receiptRes?.data || null;
        setExistingReceipt(receipt);
        // Correcting an existing receipt starts from what was actually
        // recorded; creating one starts from the case estimate.
        if (receipt) {
          setRoundingUpAmount(receipt.roundingUpAmount || 0);
          setProcessingFeePercent(receipt.processingFeePercent || 0);
        } else {
          if (c.roundingUpAmount != null) {
            setRoundingUpAmount(c.roundingUpAmount);
          }
          if (c.processingFee != null) {
            setProcessingFeePercent(c.processingFee);
          }
        }

        // Fetch toggle state
        try {
          const branchId = c.branch?._id || c.branchId;
          if (branchId) {
            fetchBranchProcessingFeeToggleVisibility(branchId)
              .then(res => {
                if (res?.data?.data?.isVisible !== undefined) {
                  setCanViewProcessingFee(res.data.data.isVisible);
                  // Hiding the fee from this user must not silently delete it
                  // from a receipt that already charges one — only zero it on a
                  // fresh receipt, where there is nothing to preserve.
                  if (!res.data.data.isVisible && !receipt) {
                    setProcessingFeePercent(0);
                  }
                }
              })
              .catch(err => console.error('Failed to fetch Processing Fee visibility:', err));
          }
        } catch (e) {
          console.error("Error fetching fee visibility", e);
        }

        if (receipt) {
          // Restore the receipt exactly as valued — including a manually
          // adjusted dust deduction, which must not be recomputed away.
          setRemarks(receipt.remarks || '');
          setValuationDate(receipt.valuationDate ? dayjs(receipt.valuationDate) : dayjs());
          setGoldImages(receipt.goldImages || []);
          setGoldItems((receipt.finalGoldItems || []).map((item) => recalcCaseGoldItem({
            qty: item.qty,
            itemName: item.itemName || '',
            purityName: item.purityName || DEFAULT_PURITY,
            purity: item.purity || 0,
            grossWeight: item.grossWeight || 0,
            stoneWaxDeduction: item.stoneWaxDeduction || 0,
            dustDeduction: item.dustDeduction || 0,
            netWeight: item.netWeight || 0,
            estimatedRatePerGram: item.finalRatePerGram || 0,
            totalPrice: item.totalPrice || 0,
            _dustDeductionOverride: true,
          }, { skipDustRecalc: true })));
        } else if (c.goldItems && c.goldItems.length > 0) {
          const defaultRateObj = rate?.rates?.find(r => r.purity === DEFAULT_PURITY);
          const defaultRatePerGram = getChannelRate(defaultRateObj, 'pledge');
          const preloaded = c.goldItems.map((item) => {
            const rateToUse = item.estimatedRatePerGram || defaultRatePerGram;
            const updated = {
              qty: item.qty,
              itemName: item.itemName || '',
              purityName: item.purityName || DEFAULT_PURITY,
              // Purity is never carried over from the case estimate — it is
              // typed in from the receipt-time test.
              purity: 0,
              grossWeight: item.grossWeight || 0,
              stoneWaxDeduction: item.stoneWaxDeduction || 0,
              dustDeduction: item.dustDeduction || 0,
              netWeight: item.netWeight || 0,
              estimatedRatePerGram: rateToUse,
              totalPrice: item.totalPrice || 0,
            };
            return recalcCaseGoldItem(updated);
          });
          setGoldItems(preloaded);
        } else if (rate?.rates?.length > 0) {
          // No items yet — seed empty row with live rate
          const defaultRateObj = rate.rates.find(r => r.purity === DEFAULT_PURITY);
          const defaultRate = getChannelRate(defaultRateObj, 'pledge');
          setGoldItems([recalcCaseGoldItem({ ...EMPTY_GOLD_ITEM, estimatedRatePerGram: defaultRate })]);
        }
      } catch (err) {
        console.error('Error loading case:', err);
        messageApi.error('Failed to load case');
      } finally {
        setLoading(false);
      }
    };
    loadCase();
  }, [caseId]);

  // ── Gold item helpers ──

  const handleItemChange = (index, field, value) => {
    const updated = [...goldItems];
    const updatedItem = { ...updated[index], [field]: value };

    // When purity/karate is selected, auto-fill rate with deduction applied
    if (field === 'purityName' && liveRates?.rates && Array.isArray(liveRates.rates)) {
      const rateObj = liveRates.rates.find(r => r.purity === value);
      // Apply karat deduction: displayRate = liveRate - deductionValue
      const displayRate = getChannelRate(rateObj, 'pledge');
      updatedItem.estimatedRatePerGram = displayRate;
    }

    // Keep behavior consistent with Pledge Release Case form:
    // manual dust edits are preserved, other edits auto-recalculate dust.
    const isDustDeductionChange = field === 'dustDeduction';
    const skipDustRecalc = isDustDeductionChange;

    if (!isDustDeductionChange) {
      updatedItem._dustDeductionOverride = false;
    } else {
      updatedItem._dustDeductionOverride = true;
    }

    updated[index] = recalcCaseGoldItem(updatedItem, { skipDustRecalc });
    setGoldItems(updated);
  };

  const addRow = () => {
    let defaultRate = 0;
    if (liveRates?.rates && Array.isArray(liveRates.rates)) {
      const defaultRateObj = liveRates.rates.find(r => r.purity === DEFAULT_PURITY);
      // Apply deduction to the live rate
      defaultRate = getChannelRate(defaultRateObj, 'pledge');
    }
    const newItem = recalcCaseGoldItem({
      ...EMPTY_GOLD_ITEM,
      qty: goldItems.length + 1,
      purityName: DEFAULT_PURITY,
      estimatedRatePerGram: defaultRate,
    });
    setGoldItems([...goldItems, newItem]);
  };

  const removeRow = (index) => {
    if (goldItems.length <= 1) return;
    setGoldItems(goldItems.filter((_, i) => i !== index));
  };

  const totals = calculateCaseGoldTotals(goldItems);

  // Calculate processing fee and final amount using state variables
  const grossGoldValue = totals.estimatedTotalAmount || 0;
  // Gold value and fee stay exact; only the final amount rounds (nearest rupee).
  const processingFeeAmount = calculateCaseProcessingFee(grossGoldValue, processingFeePercent);
  const finalNetAmount = calculateCaseFinalAmount(grossGoldValue, processingFeeAmount, roundingUpAmount);
  const roundOffAmount = calculateCaseRoundOff(grossGoldValue, processingFeeAmount, roundingUpAmount, finalNetAmount);

  // ── Submit ──

  const handleSubmit = async () => {
    if (!caseId) {
      messageApi.error('No case ID provided');
      return;
    }
    if (goldItems.length === 0 || goldItems.every((item) => !item.grossWeight)) {
      messageApi.error('Please add at least one gold item');
      return;
    }

    setSubmitting(true);
    try {
      // Map estimatedRatePerGram → finalRatePerGram for the backend
      const finalGoldItems = goldItems.map((item) => ({
        qty: item.qty,
        itemName: item.itemName,
        purityName: item.purityName || DEFAULT_PURITY,
        purity: item.purity,
        grossWeight: item.grossWeight,
        stoneWaxDeduction: item.stoneWaxDeduction,
        dustDeduction: item.dustDeduction,
        netWeight: item.netWeight,
        finalRatePerGram: item.estimatedRatePerGram,
        totalPrice: item.totalPrice,
      }));

      // Map uploaded images to the shape the backend expects
      const imagePayload = goldImages.map((img) => ({
        url: img.url,
        thumbnailUrl: img.thumbnailUrl,
        fileId: img.fileId,
        filePath: img.filePath,
        name: img.name,
      }));

      const payload = {
        caseId,
        finalGoldItems,
        valuationDate: valuationDate?.toISOString(),
        remarks,
        goldImages: imagePayload,
        processingFeePercent,
        roundingUpAmount,
      };

      if (isEditMode) {
        const res = await updateGoldReceipt(caseId, payload);
        messageApi.success(res.message || 'Gold receipt corrected — the case is locked again');
        // Knock-on effects of the correction: an overpayment now recoverable,
        // a reopened settlement, a restated agent commission. Each needs the
        // user's attention, so hold them on screen rather than flashing past.
        (res.warnings || []).forEach((w) => messageApi.warning(w, 10));
        setTimeout(() => navigate(`/pledge-release-cases/${caseId}`), (res.warnings || []).length ? 2000 : 800);
      } else {
        await createGoldReceipt(payload);
        messageApi.success('Gold receipt created — rate is now locked');
        setTimeout(() => navigate(`/pledge-release-cases/${caseId}`), 800);
      }
    } catch (err) {
      console.error(isEditMode ? 'Update receipt error:' : 'Create receipt error:', err);
      messageApi.error(
        err?.response?.data?.message || err?.data?.message
        || `Failed to ${isEditMode ? 'correct' : 'create'} gold receipt`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!caseId) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Alert type="error" message="No case ID provided in the URL query parameters." showIcon />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Text type="secondary">Case not found.</Text>
      </div>
    );
  }

  // ── Gates ──
  // Creating a receipt needs the case at FULLY_PAID_AWAITING_RELEASE.
  // Correcting one instead needs an approved, unspent reconciliation aimed at
  // the valuation. The backend enforces both; these just stop the user filling
  // in a form that was never going to be accepted.
  const reconciliation = caseData.reconciliation;
  const isUnlockedForReceipt = reconciliation?.status === 'UNLOCKED'
    && reconciliation?.target === 'GOLD_RECEIPT';

  if (isEditMode && (!isUnlockedForReceipt || !existingReceipt)) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Alert
          type="warning"
          showIcon
          message="This valuation is locked."
          description={
            !existingReceipt
              ? 'There is no gold receipt on this case to correct.'
              : reconciliation?.status === 'PENDING'
                ? 'A reconciliation has been raised for this case but is still awaiting approval.'
                : 'Corrections to a gold receipt need an approved reconciliation. Open the case and use the Reconcile button to request one.'
          }
        />
        <Button className="mt-3" onClick={() => navigate(`/pledge-release-cases/${caseId}`)}>
          Back to Case
        </Button>
      </div>
    );
  }

  if (!isEditMode && caseData.status !== 'FULLY_PAID_AWAITING_RELEASE') {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Alert
          type="warning"
          showIcon
          message={`Gold receipt can only be created when status is FULLY PAID AWAITING RELEASE.`}
          description={`Current status: ${caseData.status?.replace(/_/g, ' ')}`}
        />
        <Button className="mt-3" onClick={() => navigate(`/pledge-release-cases/${caseId}`)}>
          Back to Case
        </Button>
      </div>
    );
  }

  // ── Gold items table columns ──
  const goldColumns = [
    {
      title: '#',
      width: 40,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: 'Item Name',
      dataIndex: 'itemName',
      render: (v, _, idx) => (
        <Input
          value={v}
          onChange={(e) => handleItemChange(idx, 'itemName', e.target.value)}
          size="small"
        />
      ),
    },
    {
      title: 'Purity (%)',
      dataIndex: 'purity',
      width: 100,
      render: (v, _, idx) => (
        <InputNumber
          value={v}
          min={0}
          max={100}
          onChange={(val) => handleItemChange(idx, 'purity', val || 0)}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Final Rate/gm (₹)',
      dataIndex: 'estimatedRatePerGram',
      width: 140,
      render: (v, _, idx) => (
        <InputNumber
          value={v}
          min={0}
          precision={2}
          onChange={(val) => handleItemChange(idx, 'estimatedRatePerGram', val || 0)}
          size="small"
          style={{ width: '100%' }}
          prefix="₹"
        />
      ),
    },
    {
      title: 'Gross Wt. (gm)',
      dataIndex: 'grossWeight',
      width: 120,
      render: (v, _, idx) => (
        <InputNumber
          value={v}
          min={0}
          step={0.0001}
          precision={4}
          onChange={(val) => handleItemChange(idx, 'grossWeight', val || 0)}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Stone/Wax Ded.',
      dataIndex: 'stoneWaxDeduction',
      width: 120,
      render: (v, _, idx) => (
        <InputNumber
          value={v}
          min={0}
          step={0.0001}
          precision={4}
          onChange={(val) => handleItemChange(idx, 'stoneWaxDeduction', val || 0)}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Dust Ded. (gm)',
      dataIndex: 'dustDeduction',
      width: 110,
      render: (v, _, idx) => (
        <InputNumber
          value={v}
          min={0}
          step={0.0001}
          precision={4}
          onChange={(val) => handleItemChange(idx, 'dustDeduction', val || 0)}
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Net Wt. (gm)',
      dataIndex: 'netWeight',
      width: 110,
      render: (v) => <Text strong>{v?.toFixed(4)}</Text>,
    },
    {
      title: 'Total Price (₹)',
      dataIndex: 'totalPrice',
      width: 150,
      render: (v) => <strong style={{ whiteSpace: 'nowrap' }}>₹ {formatAmount4(v)}</strong>,
    },
    {
      title: '',
      width: 44,
      render: (_, __, idx) =>
        goldItems.length > 1 ? (
          <Button type="text" danger size="small" icon={<CloseOutlined />} onClick={() => removeRow(idx)} />
        ) : null,
    },
  ];

  return (
    <>
      {contextHolder}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Card>
              <Row justify="space-between" align="middle" className="mb-3">
                <Col>
                  <Title level={3} style={{ margin: 0 }}>
                    <GoldOutlined style={{ marginRight: 8 }} />
                    {isEditMode ? 'Correct Gold Receipt' : 'Create Gold Receipt'} — {caseData.caseId}
                  </Title>
                </Col>
                <Col>
                  <Space>
                    {/* Live gold rate badge */}
                    {liveRates?.rates && Array.isArray(liveRates.rates) && (
                      (() => {
                        const rateObj = liveRates.rates.find(r => r.karat === '22k');
                        return (
                          <Tooltip title={`Hallmark 916 / 22-carat — ${liveRates.date}`}>
                            <Tag
                              color="gold"
                              icon={<GoldOutlined />}
                              style={{ cursor: 'default', fontSize: 13, padding: '2px 10px' }}
                            >
                              Live: {rateObj?.karat} ₹{rateObj?.perGram?.toLocaleString('en-IN')}/gm
                            </Tag>
                          </Tooltip>
                        );
                      })()
                    )}
                    <Tooltip title="Refresh live gold rate and apply to all rows">
                      <Button
                        size="small"
                        icon={<SyncOutlined spin={rateLoading} />}
                        onClick={handleRefreshRate}
                        loading={rateLoading}
                      >
                        Refresh Rate
                      </Button>
                    </Tooltip>
                    <Button onClick={() => navigate(`/pledge-release-cases/${caseId}`)}>Back to Case</Button>
                  </Space>
                </Col>
              </Row>

              {/* Reconciliation notice — what this correction is authorised to do */}
              {isEditMode && (
                <Alert
                  type="warning"
                  showIcon
                  className="mb-3"
                  message={`Correcting receipt ${existingReceipt?.receiptId} under an approved reconciliation`}
                  description={
                    <>
                      <div><strong>Reason given:</strong> {reconciliation?.reason || '—'}</div>
                      <div style={{ marginTop: 6 }}>
                        Saving replaces the valuation and re-runs the settlement maths — what the
                        customer is owed, the profit and any unpaid agent commission all follow the
                        corrected figures. <strong>This is your one save</strong>; the case locks
                        again immediately after.
                      </div>
                    </>
                  }
                />
              )}

              {/* Case Info */}
              <Alert
                type="info"
                showIcon
                className="mb-3"
                message={
                  <Space split={<Divider type="vertical" />}>
                    <span><strong>Customer:</strong> {caseData.customerId?.name || '-'}</span>
                    <span><strong>Lender:</strong> {caseData.lenderName}</span>
                    <span><strong>Closure:</strong> ₹ {formatPrice(caseData.closureAmount || 0)}</span>
                    <span><strong>Status:</strong> {caseData.status?.replace(/_/g, ' ')}</span>
                  </Space>
                }
              />

              <Divider orientation="left">Final Gold Items (Post Valuation)</Divider>

              {/* Gold Items Table */}
              <Table
                dataSource={goldItems.map((item, idx) => ({ ...item, key: idx }))}
                columns={goldColumns}
                pagination={false}
                size="small"
                bordered
                // Fixed layout: columns keep their width as values change (no
                // jumping); Item Name takes the spare width, and below
                // 1110px the table scrolls sideways instead of squeezing.
                tableLayout="fixed"
                scroll={{ x: 1110 }}
                summary={() => (
                  <>
                    <Table.Summary.Row style={{ background: '#fafafa' }}>
                      <Table.Summary.Cell colSpan={4} align="right"><strong style={{ fontSize: '11px' }}>Totals</strong></Table.Summary.Cell>
                      <Table.Summary.Cell><strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalGrossWeight)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell><strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalStoneDeduction)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell><strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalDustDeduction)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell><strong style={{ fontSize: '11px' }}>{formatWeight4(totals.totalNetWeight)} gm</strong></Table.Summary.Cell>
                      <Table.Summary.Cell><strong style={{ fontSize: '12px' }}>₹ {formatAmount4(grossGoldValue)}</strong></Table.Summary.Cell>
                    </Table.Summary.Row>
                    {canViewProcessingFee && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={8} align="right">
                          <Space>
                            <strong style={{ fontSize: '12px' }}>Processing Fee ({processingFeePercent}%)</strong>
                            <InputNumber
                              max={100000}
                              step={0.5}
                              precision={2}
                              value={processingFeePercent}
                              onChange={(val) => setProcessingFeePercent(val || 0)}
                              size="small"
                              style={{ width: 80, fontSize: '10px' }}
                              controls={false}
                            />
                          </Space>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <strong style={{ fontSize: '12px' }}>{formatProcessingFeeDisplay(processingFeeAmount)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={8} align="right">
                        <Space>
                          <strong style={{ fontSize: '12px' }}>Rounding Up (₹)</strong>
                          <InputNumber
                            max={100000}
                            step={1}
                            precision={0}
                            value={roundingUpAmount}
                            onChange={(val) => setRoundingUpAmount(val || 0)}
                            size="small"
                            style={{ width: 80, fontSize: '10px' }}
                            controls={false}
                          />
                        </Space>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <strong style={{ fontSize: '12px' }}>{roundUpFeeDisplay(roundingUpAmount)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={8} align="right">
                        <Text type="secondary" style={{ fontSize: '12px' }}>Round Off (to nearest ₹)</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatRoundOff(roundOffAmount)}</Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row style={{ borderTop: '2px solid #d9d9d9' }}>
                      <Table.Summary.Cell colSpan={8} align="right">
                        <strong style={{ fontSize: '12px' }}>Final Net Value</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <strong style={{ fontSize: '12px', color: '#3f8600' }}>
                          {formatWholeAmount(finalNetAmount)}
                        </strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                )}
              />

              <Space className="mt-3">
                <Button icon={<PlusOutlined />} onClick={addRow}>
                  Add Item
                </Button>
              </Space>

              <Divider />

              {/* Valuation Date & Remarks */}
              <Row gutter={16} className="mb-3">
                <Col xs={24} sm={8}>
                  <Text strong>Valuation Date</Text>
                  <DatePicker
                    value={valuationDate}
                    onChange={(val) => setValuationDate(val)}
                    style={{ width: '100%', marginTop: 4 }}
                    format="DD MMM YYYY"
                  />
                </Col>
                <Col xs={24} sm={16}>
                  <Text strong>Remarks</Text>
                  <Input.TextArea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={1}
                    placeholder="Optional valuation notes"
                    style={{ marginTop: 4 }}
                  />
                </Col>
              </Row>

              {/* Gold Image Upload */}
              <Divider orientation="left"><CameraOutlined /> Gold Images</Divider>
              <Row gutter={16} className="mb-3" align="middle">
                <Col>
                  <GoldImageCapture
                    images={goldImages}
                    setImages={setGoldImages}
                    customerId={caseData?.customerId?._id || caseId}
                    driveImageType="gold-receipt"
                    driveFileNameBase="gold-receipt"
                    captureTargetKey="goldReceipt.goldImages"
                    captureTargetLabel="Gold Items — Gold Receipt"
                    captureFolderPath="/gold_item"
                  />
                </Col>
                <Col flex="auto">
                  <Text type="secondary">
                    Upload photos of the gold items for this receipt — pick a file, use the camera,
                    or scan the QR to shoot them on your phone. Each thumbnail can be cropped or repositioned.
                  </Text>
                  {goldImages.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text strong>{goldImages.length} image{goldImages.length > 1 ? 's' : ''} uploaded</Text>
                    </div>
                  )}
                </Col>
              </Row>

              <Divider />

              {/* Final Gold Value Summary Cards */}
              <Row gutter={16} className="mb-4">
                <Col xs={24} sm={6}>
                  <Card size="small" bordered>
                    <Text type="secondary">Gross Gold Value</Text>
                    <Title level={4} style={{ margin: 0 }}>
                      ₹ {formatAmount4(grossGoldValue)}
                    </Title>
                  </Card>
                </Col>
                {canViewProcessingFee && (
                  <Col xs={24} sm={6}>
                    <Card size="small" bordered style={{ background: '#fff1f0' }}>
                      <Text type="secondary">Processing Fee ({processingFeePercent}%)</Text>
                      <Title level={4} style={{ margin: 0, color: '#cf1322' }}>
                        - ₹ {formatAmount4(processingFeeAmount)}
                      </Title>
                    </Card>
                  </Col>
                )}
                <Col xs={24} sm={6}>
                  <Card size="small" bordered style={{ background: '#f6ffed' }}>
                    <Text type="secondary">Final Net Value</Text>
                    <Title level={3} style={{ margin: 0, color: '#3f8600' }}>
                      {formatWholeAmount(finalNetAmount)}
                    </Title>
                    {roundOffAmount !== 0 && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        incl. round off {formatRoundOff(roundOffAmount)}
                      </Text>
                    )}
                  </Card>
                </Col>
                <Col xs={24} sm={6}>
                  <Card size="small" bordered>
                    <Text type="secondary">Final Net Weight</Text>
                    <Title level={4} style={{ margin: 0 }}>
                      {formatWeight4(totals.totalNetWeight)} gm
                    </Title>
                  </Card>
                </Col>
              </Row>

              {/* Submit */}
              <Row justify="end">
                <Space>
                  <Button onClick={() => navigate(`/pledge-release-cases/${caseId}`)}>Cancel</Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<GoldOutlined />}
                    loading={submitting}
                    onClick={handleSubmit}
                  >
                    {isEditMode ? 'Save Correction & Re-lock' : 'Create Gold Receipt & Lock Rate'}
                  </Button>
                </Space>
              </Row>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default GoldReceiptForm;
